/**
 * JSON-LD schema builders for Graceful Garden Coaching.
 * Each returns a plain object ready to `JSON.stringify` into an `application/ld+json` <script>.
 *
 * See docs: https://schema.org (Course, Event, LocalBusiness, BreadcrumbList, FAQPage, ItemList)
 * Google's rich-results validator: https://search.google.com/test/rich-results
 */

const ORG_ID = "https://gracefulgardencoaching.com/#organization";
const PLACE_ID = "https://gracefulgardencoaching.com/#place";

const BUSINESS_CORE = {
  name: "Graceful Garden Coaching",
  legalName: "Graceful Garden Coaching",
  url: "https://gracefulgardencoaching.com",
  logo: "https://gracefulgardencoaching.com/brand-logo.png",
  image: "https://gracefulgardencoaching.com/hero-garden.jpg",
  description:
    "Hands-on homestead education for Arizona homeschool families. Registered ClassWallet Direct Pay Vendor serving Chino Valley, Prescott, and Yavapai County.",
  telephone: "",
  email: "heather@gracefulgardencoaching.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Chino Valley",
    addressRegion: "AZ",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "City", name: "Chino Valley" },
    { "@type": "City", name: "Prescott" },
    { "@type": "City", name: "Prescott Valley" },
    { "@type": "AdministrativeArea", name: "Yavapai County" },
    { "@type": "State", name: "Arizona" },
  ],
  founder: {
    "@type": "Person",
    name: "Heather Mich",
    jobTitle: "Homestead Educator",
    knowsAbout: [
      "homestead education", "gardening", "food preservation", "fermentation",
      "cheesemaking", "fiber arts", "backyard animal husbandry",
    ],
  },
};

/** LocalBusiness / Organization root schema — put on the landing page. */
export function buildLocalBusiness() {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "EducationalOrganization"],
    "@id": ORG_ID,
    ...BUSINESS_CORE,
    priceRange: "$$",
    keywords: [
      "Arizona ESA classes", "ClassWallet vendor", "homeschool field trips",
      "Chino Valley homesteading", "Yavapai County homeschool",
    ].join(", "),
  };
}

/** WebSite schema with sitelinks searchbox (helps Google Sitelinks). */
export function buildWebSite() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://gracefulgardencoaching.com/#website",
    url: "https://gracefulgardencoaching.com",
    name: "Graceful Garden Coaching",
    publisher: { "@id": ORG_ID },
  };
}

/** Person schema for Heather — attach on About page or as author on posts. */
export function buildPerson() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Heather Mich",
    jobTitle: "Homestead Educator",
    worksFor: { "@id": ORG_ID },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Chino Valley",
      addressRegion: "AZ",
      addressCountry: "US",
    },
    url: "https://gracefulgardencoaching.com/#about",
    image: "https://gracefulgardencoaching.com/about-heather.jpg",
    knowsAbout: BUSINESS_CORE.founder.knowsAbout,
  };
}

function _classStartIso(cls) {
  return cls.date_time_iso || (cls.class_date && cls.start_time
    ? `${cls.class_date}T${cls.start_time}:00-07:00`
    : null);
}

function _classEndIso(cls) {
  if (cls.class_date && cls.end_time) return `${cls.class_date}T${cls.end_time}:00-07:00`;
  const start = _classStartIso(cls);
  if (!start) return null;
  // Default to +3 hours if no end_time on legacy docs
  const d = new Date(start);
  if (isNaN(d.getTime())) return null;
  d.setHours(d.getHours() + 3);
  return d.toISOString();
}

function _classUrl(cls) {
  return `https://gracefulgardencoaching.com/register/${cls.id}`;
}

/** Course schema — one per class. */
export function buildCourse(cls) {
  const startIso = _classStartIso(cls);
  const endIso = _classEndIso(cls);
  const seatsAvailable = Math.max(0, (cls.capacity_total ?? 0) - (cls.spots_filled ?? 0));
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": `${_classUrl(cls)}#course`,
    name: cls.title,
    description: cls.description || cls.title,
    provider: { "@id": ORG_ID },
    url: _classUrl(cls),
    inLanguage: "en-US",
    educationalLevel: "K-8",
    audience: {
      "@type": "EducationalAudience",
      educationalRole: "student",
    },
    offers: {
      "@type": "Offer",
      price: cls.price_student,
      priceCurrency: "USD",
      availability: seatsAvailable > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/SoldOut",
      url: _classUrl(cls),
      validFrom: cls.created_at,
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "In-person",
      startDate: startIso,
      endDate: endIso,
      location: {
        "@type": "Place",
        "@id": PLACE_ID,
        name: "Graceful Garden Homestead",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Chino Valley",
          addressRegion: "AZ",
          addressCountry: "US",
        },
      },
      instructor: {
        "@type": "Person",
        name: "Heather Mich",
        jobTitle: "Homestead Educator",
      },
      maximumAttendeeCapacity: cls.capacity_total,
    },
  };
}

/** Event schema — dual-tagged alongside Course for date-scoped rich results. */
export function buildEvent(cls) {
  const startIso = _classStartIso(cls);
  const endIso = _classEndIso(cls);
  const seatsAvailable = Math.max(0, (cls.capacity_total ?? 0) - (cls.spots_filled ?? 0));
  return {
    "@context": "https://schema.org",
    "@type": "EducationEvent",
    "@id": `${_classUrl(cls)}#event`,
    name: cls.title,
    description: cls.description || cls.title,
    startDate: startIso,
    endDate: endIso,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    url: _classUrl(cls),
    location: {
      "@type": "Place",
      "@id": PLACE_ID,
      name: "Graceful Garden Homestead",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Chino Valley",
        addressRegion: "AZ",
        addressCountry: "US",
      },
    },
    organizer: { "@id": ORG_ID },
    offers: {
      "@type": "Offer",
      price: cls.price_student,
      priceCurrency: "USD",
      availability: seatsAvailable > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/SoldOut",
      url: _classUrl(cls),
      validFrom: cls.created_at,
    },
    maximumAttendeeCapacity: cls.capacity_total,
    remainingAttendeeCapacity: seatsAvailable,
    isAccessibleForFree: false,
  };
}

/**
 * BreadcrumbList — pass an array of `{name, url}` items in order (Home first).
 * `url` may be relative — will be resolved against the site origin.
 */
export function buildBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url.startsWith("http")
        ? it.url
        : `https://gracefulgardencoaching.com${it.url}`,
    })),
  };
}

/** FAQPage — pass an array of `{q, a}` items. Used on landing (existing FAQ block) and FAQ pages. */
export function buildFaqPage(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** ItemList — wrap a set of upcoming Courses on the landing page. Boosts topical relevance. */
export function buildCourseItemList(classes) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: classes.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Course",
        name: c.title,
        url: _classUrl(c),
        provider: { "@id": ORG_ID },
      },
    })),
  };
}
